import { Colors } from '@oy/uicore';
import { IconName } from '@oy/uiicon';
import classNames from 'classnames';
import React, { FC } from 'react';
import { formatString, makeId } from '../../../utilities/idComponent';
import IconButton from '../../Components/Button/IconButton/IconButton';
import { AttributedText, Text } from '../../Contents';
import { Spacer } from '../../Layout';
import styles from './FieldContainer.module.css';

export enum FontStyle {
  default,
  gray,
}

export type FieldCustomMessage = {
  message: string;
  color?: Colors;
  icon?: 'error';
};

type FieldContainerProps = {
  fontStyle?: FontStyle;
  isHorizontal?: boolean;
  children: React.ReactNode;
  label?: string | React.ReactNode;
  labelColor?: Colors;
  shouldUseMessageColor?: boolean;
  subLabel?: string;
  helpText?: string;
  helpMessage?: React.ReactNode;
  name?: string;
  errorMessage?: string;
  warningMessage?: string;
  customMessage?: FieldCustomMessage | null;
  helpButton?: boolean;
  helpButtonIcon?: IconName;
  onClickHelpText?: () => void;
  onClickHelpButton?: () => void;
  leftChild?: React.ReactNode;
  rightChild?: React.ReactNode;
  bottomChild?: React.ReactNode;
  isRequired?: boolean;
  errorMessageId?: string;
  leftColLength?: number;
  subLabelPosition?: 'vertical' | 'horizontal';
  helpTextAlign?: 'left' | 'right';
  boldLabel?: boolean;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

const FieldContainer: FC<FieldContainerProps> = ({
  bottomChild,
  children,
  customMessage,
  errorMessage,
  errorMessageId,
  helpButton,
  helpButtonIcon,
  helpText,
  helpMessage,
  id,
  isRequired,
  label,
  labelColor,
  leftChild,
  name,
  onClickHelpButton,
  onClickHelpText,
  rightChild,
  shouldUseMessageColor,
  subLabel,
  warningMessage,
  fontStyle = FontStyle.default,
  isHorizontal = false,
  leftColLength = 3,
  subLabelPosition = 'vertical',
  helpTextAlign = 'left',
  boldLabel = false,
  ...props
}: FieldContainerProps) => {
  const getMessageColor = () => {
    if (errorMessage) {
      return Colors.RED100;
    } else if (customMessage && customMessage.color) {
      return customMessage.color;
    }
    return Colors.BLACK100;
  };

  const getLabelColor = () => {
    if (shouldUseMessageColor) return getMessageColor();
    if (labelColor) return labelColor;
    return fontStyle === FontStyle.gray ? Colors.BLACK40 : Colors.BLACK100;
  };

  const leftColSm = `col-sm-${leftColLength}`;
  const rightColSm = `col-sm-${12 - leftColLength}`;

  return (
    <div
      id={makeId('', formatString(label), id)}
      className={classNames(
        {
          row: isHorizontal,
          'form-group': Boolean(label),
        },
        props.className,
      )}
    >
      {label && (
        <div
          className={classNames(styles.label, {
            [leftColSm]: isHorizontal,
            [styles.labelFlex]: subLabelPosition === 'horizontal',
          })}
        >
          <div className={styles.displayFlex}>
            <Text
              textStyle={
                fontStyle === FontStyle.gray || boldLabel
                  ? Text.Style.BodyMdSemibold
                  : Text.Style.BodyMdRegular
              }
              color={getLabelColor()}
            >
              {label}
            </Text>
            {isRequired && (
              <Spacer left={Spacer.Size.XXXS}>
                <Text
                  textStyle={
                    fontStyle === FontStyle.gray
                      ? Text.Style.BodyMdSemibold
                      : Text.Style.BodyMdRegular
                  }
                  color={Colors.RED100}
                >
                  *
                </Text>
              </Spacer>
            )}
            {helpButton && (
              <IconButton
                iconName={helpButtonIcon ? helpButtonIcon : 'help'}
                size={IconButton.Size.Mini}
                color={IconButton.Color.Secondary}
                buttonType={IconButton.Type.Clear}
                onClick={onClickHelpButton}
              />
            )}
          </div>
          {subLabel && (
            <Text textStyle={Text.Style.BodySmRegular} color={Colors.BLACK40}>
              {subLabel}
            </Text>
          )}
        </div>
      )}
      <div className={classNames({ [rightColSm]: isHorizontal })}>
        <div className={styles.childrenContainer}>
          {leftChild && <div className={styles.leftChild}>{leftChild}</div>}
          <div
            className={classNames(styles.children, {
              [styles.childrenRightChild]: rightChild,
            })}
          >
            {children}

            <div
              className={classNames({
                'ml-3': isHorizontal,
              })}
            >
              {helpText && (
                <Spacer>
                  <Text
                    className={classNames(styles.preWrap, {
                      [styles.clickableHelpText]: !!onClickHelpText,
                    })}
                    color={onClickHelpText ? Colors.BLUE100 : Colors.BLACK100}
                    onClick={onClickHelpText}
                    textStyle={Text.Style.BodySmRegular}
                  >
                    <AttributedText
                      value={helpText}
                      textStyle={Text.Style.BodySmRegular}
                      style={{ textAlign: helpTextAlign }}
                    />
                  </Text>
                </Spacer>
              )}
              {helpMessage && <div>{helpMessage}</div>}
              {(errorMessage || warningMessage || customMessage) && (
                <Spacer>
                  <Text
                    className={styles.preWrap}
                    textStyle={Text.Style.BodySmRegular}
                    color={getMessageColor()}
                    id={makeId(
                      'msg',
                      name ?? formatString(label),
                      errorMessageId,
                    )}
                  >
                    {errorMessage || warningMessage || customMessage?.message}
                  </Text>
                </Spacer>
              )}
            </div>
          </div>
          {rightChild && <div className={styles.rightChild}>{rightChild}</div>}
        </div>
        <div
          className={classNames({
            'ml-3': isHorizontal,
          })}
        >
          {bottomChild && <Spacer top={Spacer.Size.XXXS}>{bottomChild}</Spacer>}
        </div>
      </div>
    </div>
  );
};

export default Object.assign(FieldContainer, {
  LabelStyle: FontStyle,
});
